import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { urlMedia } from "@/lib/apercu";
import { toast } from "sonner";
import { Plane, Check, Ban, Flag, Loader2, MapPin } from "lucide-react";
import { AIR_BENGUE, libelleType } from "@/lib/airBengue";
import { formatFCFA } from "@/lib/user";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CarteModeration = ({ annonce, onDecision, enCours }) => {
  const details = annonce.air || {};
  return (
    <div
      className="flex gap-4 rounded-2xl border border-[#E5DFD5] bg-white p-4"
      data-testid={`air-moderation-${annonce.id}`}
    >
      {annonce.photos?.[0] && (
        <img src={urlMedia(annonce.photos[0].url)} alt="" className="h-20 w-20 shrink-0 rounded-xl object-cover" />
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-bold text-stone-900">{details.titre || "Sans titre"}</p>
        <p className="mt-1 flex items-center gap-1.5 text-xs font-semibold text-stone-500">
          <MapPin size={12} /> {details.ville} · {details.pays} ·{" "}
          <span style={{ color: AIR_BENGUE.color }}>{libelleType(details.type_prestation)}</span>
        </p>
        <p className="mt-1 text-xs text-stone-500">
          {annonce.palier} · {details.tarif ? formatFCFA(details.tarif) : "tarif sur demande"} ·{" "}
          {annonce.telephones?.length || 0} numéro(s)
          {annonce.signalements > 0 && (
            <span className="ml-2 inline-flex items-center gap-1 font-bold text-red-600">
              <Flag size={11} /> {annonce.signalements} signalement(s)
            </span>
          )}
        </p>
        {annonce.texte && <p className="mt-2 line-clamp-2 text-xs text-stone-600">{annonce.texte}</p>}
        <div className="mt-3 flex gap-2">
          <button
            onClick={() => onDecision(annonce.id, "publier")}
            disabled={enCours}
            data-testid={`air-publier-${annonce.id}`}
            className="inline-flex items-center gap-1.5 rounded-full bg-[#0A5C36] px-4 py-2 text-xs font-bold text-white disabled:opacity-50"
          >
            <Check size={12} /> Publier
          </button>
          <button
            onClick={() => onDecision(annonce.id, "refuser")}
            disabled={enCours}
            data-testid={`air-refuser-${annonce.id}`}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#E5DFD5] px-4 py-2 text-xs font-bold text-stone-700 disabled:opacity-50"
          >
            <Ban size={12} /> Refuser
          </button>
        </div>
      </div>
    </div>
  );
};

export const ModerationAirBengue = ({ adminId }) => {
  const [data, setData] = useState(null);
  const [enCours, setEnCours] = useState(false);

  const charger = useCallback(() => {
    axios
      .get(`${API}/admin/air-bengue/moderation`, { params: { admin_id: adminId } })
      .then((r) => setData(r.data))
      .catch(() => setData({ en_attente: [], signalees: [], signalements: [] }));
  }, [adminId]);

  useEffect(charger, [charger]);

  const decider = async (annonceId, decision) => {
    setEnCours(true);
    try {
      await axios.post(`${API}/admin/air-bengue/${annonceId}/moderer`, null, {
        params: { admin_id: adminId, decision },
      });
      toast.success(decision === "publier" ? "Annonce publiée" : "Annonce refusée");
      charger();
    } catch {
      toast.error("Action impossible.");
    } finally {
      setEnCours(false);
    }
  };

  if (!data) {
    return (
      <p className="mt-10 flex items-center gap-2 text-sm text-stone-400">
        <Loader2 size={14} className="animate-spin" /> Chargement de la modération…
      </p>
    );
  }

  const aTraiter = [...data.en_attente, ...data.signalees];

  return (
    <section className="mt-10" data-testid="air-moderation-panel">
      <div className="flex items-center gap-2">
        <Plane size={16} className="-rotate-45" style={{ color: AIR_BENGUE.color }} />
        <h2 className="font-heading text-base font-bold text-stone-900 md:text-lg">
          Modération FodindinAir-Bengue
        </h2>
        <span
          className="rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider"
          style={{ background: AIR_BENGUE.colorSoft, color: AIR_BENGUE.color }}
          data-testid="air-moderation-compteur"
        >
          {aTraiter.length} à traiter
        </span>
      </div>

      {aTraiter.length === 0 ? (
        <p className="mt-4 text-sm text-stone-500" data-testid="air-moderation-vide">
          Aucune annonce en attente. Tout est à jour.
        </p>
      ) : (
        <div className="mt-4 space-y-3">
          {aTraiter.map((a) => (
            <CarteModeration key={a.id} annonce={a} onDecision={decider} enCours={enCours} />
          ))}
        </div>
      )}

      {data.signalements?.length > 0 && (
        <div className="mt-6 rounded-2xl border border-[#E5DFD5] bg-white p-4">
          <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-stone-500">
            Motifs signalés par les membres
          </p>
          <ul className="mt-3 space-y-2" data-testid="air-signalements-liste">
            {data.signalements.slice(0, 10).map((s) => (
              <li key={s.id} className="text-xs text-stone-600">
                <Flag size={11} className="mr-1.5 inline text-red-600" />
                {s.motif}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};
